'use client';

import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { createContext, forwardRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '../lib/utils';

import { DROPDOWN_MENU_ITEM_BASE_CLASSES } from './dropdown-menu';

type SubInlineContextValue = {
  isSubMenuOpen: boolean;
  triggerRef: React.RefObject<HTMLDivElement | null>;
  openSubMenu: () => void;
  closeSubMenu: () => void;
};

const SubInlineContext = createContext<SubInlineContextValue | undefined>(undefined);

const useSubInline = () => {
  const context = useContext(SubInlineContext);
  if (!context) {
    throw new Error('useSubInline must be used within DropdownMenuSubInline');
  }
  return context;
};

type DropdownMenuSubInlineProps = {
  children: React.ReactNode;
  defaultOpen?: boolean;
};

const DropdownMenuSubInline = ({ children, defaultOpen = false }: DropdownMenuSubInlineProps) => {
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(defaultOpen);
  const triggerRef = useRef<HTMLDivElement>(null);

  const openSubMenu = useCallback(() => {
    setIsSubMenuOpen(true);
  }, []);

  const closeSubMenu = useCallback(() => {
    setIsSubMenuOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      isSubMenuOpen,
      triggerRef,
      openSubMenu,
      closeSubMenu,
    }),
    [isSubMenuOpen, openSubMenu, closeSubMenu],
  );

  return <SubInlineContext.Provider value={value}>{children}</SubInlineContext.Provider>;
};

type DropdownMenuSubInlineTriggerProps = React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
  inset?: boolean;
};

const DropdownMenuSubInlineTrigger = forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  DropdownMenuSubInlineTriggerProps
>(({ className, inset, children, onSelect, ...props }, ref) => {
  const { openSubMenu, triggerRef } = useSubInline();

  const handleSelect = (e: Event) => {
    e.preventDefault();
    openSubMenu();
    onSelect?.(e);
  };

  const handleRef = useCallback(
    (node: HTMLDivElement | null) => {
      (triggerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [ref, triggerRef],
  );

  return (
    <DropdownMenuPrimitive.Item
      ref={handleRef}
      className={cn(DROPDOWN_MENU_ITEM_BASE_CLASSES, inset && 'pl-8', className)}
      onSelect={handleSelect}
      {...props}
    >
      {children}
      <ChevronRight className="ml-auto size-4" />
    </DropdownMenuPrimitive.Item>
  );
});
DropdownMenuSubInlineTrigger.displayName = 'DropdownMenuSubInlineTrigger';

type DropdownMenuSubInlineBackProps = React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
  children: React.ReactNode;
  onClick?: () => void;
};

const DropdownMenuSubInlineBack = forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  DropdownMenuSubInlineBackProps
>(({ className, children, onClick, ...props }, ref) => {
  const { closeSubMenu, triggerRef } = useSubInline();

  const handleSelect = (e: Event) => {
    e.preventDefault();
    closeSubMenu();
    requestAnimationFrame(() => {
      triggerRef.current?.focus();
    });
    onClick?.();
  };

  return (
    <DropdownMenuPrimitive.Item
      ref={ref}
      className={cn(DROPDOWN_MENU_ITEM_BASE_CLASSES, className)}
      onSelect={handleSelect}
      {...props}
    >
      <ChevronLeft className="size-4" />
      {children}
    </DropdownMenuPrimitive.Item>
  );
});
DropdownMenuSubInlineBack.displayName = 'DropdownMenuSubInlineBack';

type DropdownMenuSubInlineContentProps = {
  children: React.ReactNode;
  className?: string;
};

const TRANSITION_DURATION = '150ms';

const toggleSiblingVisibility = (element: HTMLElement, hide: boolean) => {
  const parent = element.closest('[role="menu"]');
  if (!(parent instanceof HTMLElement)) {
    return;
  }

  Array.from(parent.children).forEach(child => {
    if (child instanceof HTMLElement && child !== element && !child.contains(element)) {
      if (hide) {
        child.dataset.hiddenBySubmenu = 'true';
        child.style.transition = `opacity ${TRANSITION_DURATION}, transform ${TRANSITION_DURATION}`;
        child.style.opacity = '0';
        child.style.transform = 'translateX(-20px)';
        child.style.pointerEvents = 'none';
      } else if (child.dataset.hiddenBySubmenu === 'true') {
        delete child.dataset.hiddenBySubmenu;
        child.style.opacity = '';
        child.style.transform = '';
        child.style.pointerEvents = '';
      }
    }
  });
};

const adjustDropdownHeight = (
  submenu: HTMLElement,
  dropdown: HTMLElement,
  shouldExpand: boolean,
  originalHeight: string,
): number | null => {
  if (!shouldExpand) {
    dropdown.style.height = originalHeight;
    dropdown.classList.remove('overflow-hidden', 'transition-[height]', 'duration-150');
    submenu.style.height = '';
    submenu.classList.remove('overflow-auto');
    submenu.style.maxHeight = '';
    return null;
  }

  const rafId = requestAnimationFrame(() => {
    const computedMaxHeight = getComputedStyle(dropdown).maxHeight;
    const maxHeight = computedMaxHeight !== 'none' ? parseFloat(computedMaxHeight) : Infinity;

    const submenuHeight = submenu.scrollHeight;
    const finalHeight = Math.min(submenuHeight, maxHeight);

    dropdown.classList.add('transition-[height]', 'duration-150');
    dropdown.style.height = `${finalHeight}px`;
    dropdown.classList.add('overflow-hidden');

    if (submenuHeight > maxHeight) {
      submenu.style.height = `${maxHeight}px`;
      submenu.style.maxHeight = `${maxHeight}px`;
      submenu.classList.add('overflow-auto');
    } else {
      submenu.style.height = `${submenuHeight}px`;
      submenu.classList.remove('overflow-auto');
    }
  });

  return rafId;
};

const DropdownMenuSubInlineContent = ({ children, className }: DropdownMenuSubInlineContentProps) => {
  const { isSubMenuOpen } = useSubInline();
  const contentRef = useRef<HTMLDivElement>(null);
  const originalHeightRef = useRef<string | null>(null);
  const rafIdRef = useRef<number | null>(null);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!contentRef.current) {
      return;
    }

    const focusableItems = Array.from(
      contentRef.current.querySelectorAll('[role="menuitem"]:not([disabled])'),
    ) as HTMLElement[];

    const currentIndex = focusableItems.indexOf(document.activeElement as HTMLElement);

    if (e.key === 'ArrowDown' && currentIndex === focusableItems.length - 1) {
      e.preventDefault();
      e.stopPropagation();
      requestAnimationFrame(() => {
        focusableItems[focusableItems.length - 1]?.focus();
      });
    } else if (e.key === 'ArrowUp' && currentIndex === 0) {
      e.preventDefault();
      e.stopPropagation();
      requestAnimationFrame(() => {
        focusableItems[0]?.focus();
      });
    }
  };

  useEffect(() => {
    const submenu = contentRef.current;
    if (!submenu) {
      return;
    }

    const dropdown = submenu.closest('[role="menu"]');
    if (!(dropdown instanceof HTMLElement)) {
      return;
    }

    dropdown.classList.add('overflow-x-hidden');

    if (isSubMenuOpen) {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }

      dropdown.dataset.submenuOpen = 'true';
      dropdown.scrollTop = 0;
      if (originalHeightRef.current === null) {
        originalHeightRef.current = dropdown.style.height || 'auto';
        rafIdRef.current = adjustDropdownHeight(submenu, dropdown, true, '');
      }
      toggleSiblingVisibility(submenu, true);

      const preventDropdownScroll = (e: Event) => {
        e.preventDefault();
        dropdown.scrollTop = 0;
      };

      dropdown.addEventListener('scroll', preventDropdownScroll);

      return () => {
        dropdown.removeEventListener('scroll', preventDropdownScroll);
        delete dropdown.dataset.submenuOpen;
        if (rafIdRef.current !== null) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
        if (originalHeightRef.current !== null) {
          adjustDropdownHeight(submenu, dropdown, false, originalHeightRef.current);
          originalHeightRef.current = null;
        }
        toggleSiblingVisibility(submenu, false);
      };
    } else {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }

      delete dropdown.dataset.submenuOpen;
      if (originalHeightRef.current !== null) {
        adjustDropdownHeight(submenu, dropdown, false, originalHeightRef.current);
        originalHeightRef.current = null;
      }
      toggleSiblingVisibility(submenu, false);
    }
  }, [isSubMenuOpen]);

  return (
    <div
      ref={contentRef}
      {...(!isSubMenuOpen && { inert: true })}
      className={cn(
        'absolute left-0 top-0 z-10 w-full bg-popover p-1 transition-all duration-150',
        isSubMenuOpen
          ? 'pointer-events-auto translate-x-0 opacity-100'
          : 'pointer-events-none h-0 translate-x-5 overflow-hidden opacity-0',
        className,
      )}
      tabIndex={-1}
      onKeyDownCapture={handleKeyDown}
    >
      {children}
    </div>
  );
};
DropdownMenuSubInlineContent.displayName = 'DropdownMenuSubInlineContent';

export { DropdownMenuSubInline, DropdownMenuSubInlineTrigger, DropdownMenuSubInlineContent, DropdownMenuSubInlineBack };
